import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Input, Select, Modal, Form, Tooltip, Tabs, Divider, Descriptions, Spin, message } from 'antd';
import { ReloadOutlined, PlusOutlined, SearchOutlined, PoweroffOutlined, EditOutlined, EyeOutlined, EyeInvisibleOutlined, WalletOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../api/config';
import {
  useWallets,
  useCreateWallet,
  useUpdateWalletStatus,
  useSupportedCoins,
  useSupportedNetworks,
  useCoinNetworks
} from '../hooks/useWalletManagement';
import { showSuccessNotification, showErrorNotification } from '../utils/notifications';
import SendTransactionModal from '../components/wallet/SendTransactionModal';
import { WithdrawalModal } from '../components/wallet/withdrawal';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const WalletManagementPage: React.FC = () => {
  // Use the wallet namespace for translations
  const { t } = useTranslation('wallet');

  // State for filters
  const [searchText, setSearchText] = useState<string>('');
  const [coinFilter, setCoinFilter] = useState<string>('');
  const [networkFilter, setNetworkFilter] = useState<string>('');
  const [walletTypeFilter, setWalletTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // State for form dependencies
  const [availableCoinNetworks, setAvailableCoinNetworks] = useState<any[]>([]);

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // State for modals
  const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isBalanceModalVisible, setIsBalanceModalVisible] = useState<boolean>(false);
  const [selectedWallet, setSelectedWallet] = useState<any>(null);
  const [createdWallet, setCreatedWallet] = useState<any>(null);
  const [isResultModalVisible, setIsResultModalVisible] = useState<boolean>(false);

  // State for balance check
  const [balanceData, setBalanceData] = useState<any>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState<boolean>(false);

  // State for send transaction modal
  const [isSendModalVisible, setIsSendModalVisible] = useState<boolean>(false);
  const [sendWalletData, setSendWalletData] = useState<any>({
    id: '',
    address: '',
    coin: '',
    network: '',
    user_id: '',
    is_evm: 1
  });

  // State for withdrawal modal
  const [isWithdrawalModalVisible, setIsWithdrawalModalVisible] = useState<boolean>(false);
  const [withdrawalWalletData, setWithdrawalWalletData] = useState<any>({
    id: '',
    address: '',
    coin: '',
    network: '',
    user_id: '',
    is_evm: 1
  });

  // State for form
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // State for private key visibility
  const [privateKeyVisible, setPrivateKeyVisible] = useState<boolean>(false);
  const [seedVisible, setSeedVisible] = useState<boolean>(false);

  // State for generated wallet values in the add modal
  const [generatedPublicAddress, setGeneratedPublicAddress] = useState<string>('');
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState<string>('');
  const [generatedSeed, setGeneratedSeed] = useState<string>('');
  const [walletGenerated, setWalletGenerated] = useState<boolean>(false);



  // Calculate skip value for pagination
  const skip = (currentPage - 1) * pageSize;

  // Build filters object
  const filters: any = {};
  if (coinFilter) filters.coin_id = coinFilter;
  if (networkFilter) filters.network_id = networkFilter;
  if (walletTypeFilter) filters.wallet_type = walletTypeFilter;
  if (statusFilter) filters.status = statusFilter;

  // Handle search text: if it's a number, use it as user_id filter; otherwise use as searchTerm
  const trimmedSearchText = searchText.trim();
  let searchTerm = '';
  if (trimmedSearchText) {
    // Check if the search text is a valid integer (user ID)
    const userId = parseInt(trimmedSearchText, 10);
    if (!isNaN(userId) && userId.toString() === trimmedSearchText) {
      // It's a valid integer, add it to filters as user_id
      filters.user_id = userId;
    } else {
      // It's not a number, use it as searchTerm (for address searches)
      searchTerm = trimmedSearchText;
    }
  }

  // Fetch wallets with pagination and filtering
  const { data: walletsData, isLoading, refetch } = useWallets(
    filters,
    pageSize,
    skip,
    searchTerm
  );

  // Fetch supported networks, coins, and coin networks
  const { data: networksData, isLoading: isLoadingNetworks } = useSupportedNetworks();
  const { data: coinsData, isLoading: isLoadingCoins } = useSupportedCoins();
  const { data: coinNetworksData, isLoading: isLoadingCoinNetworks } = useCoinNetworks();

  // Log network data when it's loaded
  useEffect(() => {
    if (networksData) {
      console.log('Networks data loaded:', networksData);
      // The network data from /api/bit/v1/network endpoint has a different structure
      // It returns { data: [...networks] } instead of { networks: [...networks] }
      const networks = networksData.data || [];
      if (networks.length > 0) {
        console.log('First network item:', networks[0]);
      }
    }
  }, [networksData]);

  // Mutations for wallet operations
  const createWalletMutation = useCreateWallet();
  const updateWalletStatusMutation = useUpdateWalletStatus();

  // Function to update available coin networks based on selected coin and network
  const updateAvailableCoinNetworks = () => {
    const coinId = form.getFieldValue('coin_id');
    const networkId = form.getFieldValue('network_id');

    console.log('Updating available coin networks with coinId:', coinId, 'networkId:', networkId);

    if (coinId && networkId && coinNetworksData?.data) {
      // Filter coin networks that match both coin and network
      const filteredCoinNetworks = coinNetworksData.data.filter((cn: any) =>
        cn.coin_id === coinId && cn.network_id === networkId
      );

      console.log('Filtered coin networks:', filteredCoinNetworks);
      setAvailableCoinNetworks(filteredCoinNetworks);

      // If there's only one match, auto-select it
      if (filteredCoinNetworks.length === 1) {
        console.log('Auto-selecting coin network:', filteredCoinNetworks[0]._id);
        form.setFieldsValue({ coin_network_id: filteredCoinNetworks[0]._id });
      } else if (filteredCoinNetworks.length === 0) {
        // Clear the selection if no matches
        console.log('No matching coin networks found, clearing selection');
        form.setFieldsValue({ coin_network_id: undefined });
      }
    } else {
      console.log('Missing coinId or networkId, clearing coin network selection');
      setAvailableCoinNetworks([]);
      form.setFieldsValue({ coin_network_id: undefined });
    }
  };

  // Function to handle adding a new wallet
  const handleAdd = () => {
    form.resetFields();
    setAvailableCoinNetworks([]);
    setGeneratedPublicAddress('');
    setGeneratedPrivateKey('');
    setGeneratedSeed('');
    setWalletGenerated(false);
    setIsAddModalVisible(true);
  };

  // Function to handle editing a wallet
  const handleEdit = async (wallet: any) => {
    try {
      // Fetch the complete wallet information from the server
      console.log('Fetching complete wallet details for editing:', wallet._id);
      console.log('Initial wallet data:', wallet);

      // Use the existing wallet data as a fallback
      let completeWallet = { ...wallet };

      try {
        // Use POST request to fetch wallet details (like in legacy dashboard)
        // This is the correct endpoint based on the legacy dashboard implementation
        const response = await axios.post(`${API_BASE}/wallet/admin/Wallets`, {
          filters: { _id: wallet._id },
          limit: 1,
          skip: 0
        });
        console.log('POST API response for wallet details:', response.data);

        if (response.data) {
          // Check if the response has data property
          const responseData = response.data.data || response.data;

          // Extract the wallet from the response
          if (responseData && responseData.wallets && responseData.wallets.length > 0) {
            completeWallet = responseData.wallets[0];
            console.log('Successfully fetched complete wallet details:', completeWallet);
          } else {
            console.warn('Wallet not found in response:', response.data);
          }
        }
      } catch (error) {
        console.error('Error fetching complete wallet details:', error);
        // Continue with the existing wallet data
      }

      // Set the selected wallet with complete data
      setSelectedWallet(completeWallet);

      // Pre-fill the form fields
      editForm.setFieldsValue({
        status: completeWallet.status
      });

      // Show the edit modal
      setIsEditModalVisible(true);
    } catch (error) {
      console.error('Error preparing wallet for editing:', error);
      showErrorNotification(
        t('editError', 'Failed to edit wallet'),
        error instanceof Error ? error.message : String(error)
      );
    }
  };

  // Function to handle toggling wallet status
  const handleToggleStatus = async (wallet: any) => {
    const newStatus = wallet.status === 'Active' ? 'Inactive' : 'Active';
    const action = wallet.status === 'Active' ? 'deactivate' : 'activate';

    // Show confirmation dialog like in legacy dashboard
    Modal.confirm({
      title: t('confirmStatusChange', 'Confirm Status Change'),
      content: `${t('confirmStatusChangeMessage', 'Are you sure you want to')} ${action} ${t('thisWallet', 'this wallet?')}`,
      okText: t('yes', 'Yes'),
      cancelText: t('no', 'No'),
      onOk: async () => {
        try {
          // Show loading notification
          showSuccessNotification(
            t('processing', 'Processing'),
            `${action.charAt(0).toUpperCase() + action.slice(1)}${t('processingStatusChange', 'ing wallet...')}`
          );

          // Call API to update wallet status
          await updateWalletStatusMutation.mutateAsync({ id: wallet._id, status: newStatus });

          // Show success notification
          showSuccessNotification(
            t('statusUpdateSuccess', 'Wallet status updated'),
            t(`wallet.${action}Success`, `Wallet successfully ${action}d`)
          );
        } catch (error) {
          console.error('Error updating wallet status:', error);
          showErrorNotification(
            t('statusUpdateError', 'Failed to update wallet status'),
            error instanceof Error ? error.message : String(error)
          );
        }
      }
    });
  };

  // Function to handle form submission for adding a wallet
  const handleAddFormSubmit = async (values: any) => {
    try {
      // Determine if the network is EVM-compatible
      const selectedNetwork = networksData?.data?.find((network: any) => network._id === values.network_id);
      const isEvm = selectedNetwork?.is_evn === 'Yes' ? 1 : 0;

      // Get the coin symbol
      const selectedCoin = coinsData?.data?.find((coin: any) => coin._id === values.coin_id);

      // Get the selected coin network
      const selectedCoinNetwork = coinNetworksData?.data?.find((cn: any) => cn._id === values.coin_network_id);

      // Extract network name - use the full_name property as in legacy dashboard
      let networkName = selectedNetwork?.full_name || '';

      // Log the values for debugging
      console.log('Creating wallet with coin:', selectedCoin?.symbol, 'network:', networkName);
      console.log('Selected network:', selectedNetwork);
      console.log('Selected coin:', selectedCoin);
      console.log('Selected coin network:', selectedCoinNetwork);

      // Create wallet data object (matches legacy dashboard)
      // Removed is_new parameter to let backend determine whether to return existing wallet
      const walletData = {
        network: networkName,
        is_evm: isEvm,
        coin: selectedCoin?.symbol || '',
        userid: parseInt(values.user_id),
        type: values.wallet_type,
        status: values.status,
        memo: values.memo || null,
        coin_network_id: selectedCoinNetwork?._id
      };

      console.log('Wallet data for API request:', walletData);

      // Show loading notification
      showSuccessNotification(
        t('generating', 'Generating wallet...'),
        t('generatingMessage', 'Please wait while the wallet is being generated')
      );

      // Store the created wallet data
      const result = await createWalletMutation.mutateAsync(walletData);

      // Check if the response has data property (some API responses wrap the wallet in a data property)
      const walletResult = result.data || result;

      console.log('Wallet creation response:', result);
      console.log('Extracted wallet result:', walletResult);
      console.log('Is existing wallet?', walletResult.isExisting ? 'Yes' : 'No');
      console.log('Wallet address:', walletResult.public_address || walletResult.address);

      // Store the created wallet
      setCreatedWallet(walletResult);

      // Update the state variables with the generated values
      setGeneratedPublicAddress(walletResult.public_address || walletResult.address || '');
      setGeneratedPrivateKey(walletResult.private_key || walletResult.privateKey || '');
      setGeneratedSeed(walletResult.seed || walletResult.mnemonic || '');
      setWalletGenerated(true);

      // Show success message - handle both new and existing wallet cases
      if (walletResult.isExisting) {
        showSuccessNotification(
          t('existingWallet', 'Existing Wallet'),
          t('existingWalletMessage', 'An existing wallet was found and returned')
        );
      } else {
        showSuccessNotification(
          t('createSuccess', 'Wallet created'),
          t('createSuccessMessage', 'New wallet has been successfully created')
        );
      }

      // Refresh the wallet list
      refetch();
    } catch (error) {
      console.error('Error creating wallet:', error);
      showErrorNotification(
        t('createError', 'Failed to create wallet'),
        error instanceof Error ? error.message : String(error)
      );
    }
  };

  // Function to handle closing the add modal
  const handleAddModalClose = () => {
    if (walletGenerated) {
      // If a wallet was generated, show the result modal
      setIsAddModalVisible(false);
      setIsResultModalVisible(true);
      // Reset visibility states
      setPrivateKeyVisible(false);
      setSeedVisible(false);
    } else {
      // If no wallet was generated, just close the modal
      setIsAddModalVisible(false);
    }
  };

  // Function to handle form submission for editing a wallet
  const handleEditFormSubmit = async (values: any) => {
    try {
      if (!selectedWallet) return;

      await updateWalletStatusMutation.mutateAsync({ id: selectedWallet._id, status: values.status });

      showSuccessNotification(
        t('statusUpdateSuccess', 'Wallet status updated'),
        t('statusUpdateSuccessMessage', 'Wallet status has been successfully updated')
      );
      setIsEditModalVisible(false);
      setPrivateKeyVisible(false);
      setSeedVisible(false);
    } catch (error) {
      console.error('Error updating wallet status:', error);
      showErrorNotification(
        t('statusUpdateError', 'Failed to update wallet status'),
        error instanceof Error ? error.message : String(error)
      );
    }
  };

  // Function to handle send transaction button click
  const handleSendTransaction = (wallet: any) => {
    // Only allow sending from active wallets
    if (wallet.status !== 'Active') {
      showErrorNotification(
        t('sendTransaction.inactiveWallet', 'Inactive Wallet'),
        t('sendTransaction.inactiveWalletMessage', 'You can only send transactions from active wallets')
      );
      return;
    }

    // Prepare wallet data for the send transaction modal
    const walletData = {
      id: wallet._id,
      address: wallet.public_address,
      coin: wallet.coin_id?.symbol || '',
      network: wallet.network_id?.full_name || '',
      user_id: wallet.user_id,
      is_evm: wallet.network_id?.is_evn === 'Yes' ? 1 : 0
    };

    // Set the wallet data and show the modal
    setSendWalletData(walletData);
    setIsSendModalVisible(true);
  };

  // Function to handle withdrawal button click
  const handleCreateWithdrawal = (wallet: any) => {
    // Only allow withdrawals from active wallets
    if (wallet.status !== 'Active') {
      showErrorNotification(
        t('withdrawal.inactiveWallet', 'Inactive Wallet'),
        t('withdrawal.inactiveWalletMessage', 'You can only create withdrawals from active wallets')
      );
      return;
    }

    // Prepare wallet data for the withdrawal modal
    const walletData = {
      id: wallet._id,
      address: wallet.public_address,
      coin: wallet.coin_id?.symbol || '',
      network: wallet.network_id?.full_name || '',
      user_id: wallet.user_id,
      is_evm: wallet.network_id?.is_evn === 'Yes' ? 1 : 0
    };

    // Set the wallet data and show the modal
    setWithdrawalWalletData(walletData);
    setIsWithdrawalModalVisible(true);
  };

  // Function to handle checking wallet balance
  const handleBalanceCheck = async (wallet: any) => {
    try {
      // Set the selected wallet
      setSelectedWallet(wallet);

      // Set loading state
      setIsLoadingBalance(true);
      setBalanceData(null);

      // Show the balance modal immediately with loading state
      setIsBalanceModalVisible(true);

      // Determine if the network is EVM-compatible
      const isEvm = wallet.network_id?.is_evn === 'Yes' ? 1 : 0;

      console.log('Checking all balances for wallet:', {
        address: wallet.public_address,
        network: wallet.network_id?.full_name || '',
        coin: wallet.coin_id?.symbol || '',
        isEvm
      });

      // Make API request to check all balances (native + tokens)
      const response = await axios.post(`${API_BASE}/wallet/AllBalances`, {
        address: wallet.public_address,
        network: wallet.network_id?.full_name || '',
        is_evm: isEvm
      });

      console.log('All balances API response:', response.data);

      // Handle different response formats
      if (response.data.status === false) {
        if (response.data.error) {
          throw new Error(response.data.error.message || 'Failed to check balances');
        } else if (response.data.data) {
          throw new Error(typeof response.data.data === 'string' ? response.data.data : 'Failed to check balances');
        } else {
          throw new Error('Failed to check balances: RPC may be unavailable');
        }
      }

      // Store the balance data
      setBalanceData(response.data);

      // Refetch wallet list to update the "Last known balance" column in the table
      // The balance check API updates the last_known_balance in the database,
      // so we need to refresh the wallet list to show the updated value
      refetch();
    } catch (error) {
      console.error('Error checking wallet balances:', error);
      showErrorNotification(
        t('balanceCheckError', 'Failed to check balances'),
        error instanceof Error ? error.message : String(error)
      );

      // Close the modal on error
      setIsBalanceModalVisible(false);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  // Function to handle tab change
  const handleTabChange = (activeKey: string) => {
    if (activeKey === 'all') {
      setWalletTypeFilter('');
    } else {
      setWalletTypeFilter(activeKey);
    }
    setCurrentPage(1);
  };

  // Table columns
  const columns = [
    {
      title: t('publicAddress', 'Public Address'),
      dataIndex: 'public_address',
      key: 'public_address',
      render: (text: string) => (
        <Tooltip title={t('clickToCopy', 'Click to copy address')}>
          <span
            style={{
              fontFamily: 'monospace',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center'
            }}
            onClick={() => {
              navigator.clipboard.writeText(text);
              message.success(t('addressCopied', 'Address copied to clipboard'));
            }}
          >
            {text.substring(0, 8)}...{text.substring(text.length - 8)}
            <CopyOutlined style={{ marginLeft: 5, fontSize: '12px', color: '#1890ff' }} />
          </span>
        </Tooltip>
      )
    },
    {
      title: t('coin', 'Coin'),
      dataIndex: 'coin_id',
      key: 'coin_id',
      render: (coin_id: any, record: any) => {
        const symbol = coin_id?.symbol || 'N/A';
        const fullName = coin_id?.full_name || '';
        return (
          <Tooltip title={fullName}>
            <Tag color="blue">{symbol}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: t('network', 'Network'),
      dataIndex: 'network_id',
      key: 'network_id',
      render: (network_id: any, record: any) => {
        const title = network_id?.title || 'N/A';
        const fullName = network_id?.full_name || '';
        return (
          <Tooltip title={fullName}>
            <Tag color="purple">{title}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: t('walletType', 'Wallet Type'),
      dataIndex: 'wallet_type',
      key: 'wallet_type',
      render: (text: string) => {
        const color = text === 'hot' ? 'red' : text === 'warm' ? 'orange' : 'green';
        return <Tag color={color}>{text.toUpperCase()}</Tag>;
      }
    },
    {
      title: t('userId', 'User ID'),
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: t('statusLabel', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (text: string) => {
        const color = text === 'Active' ? 'green' : 'red';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: t('lastKnownBalance', 'Last Known Balance'),
      dataIndex: 'last_known_balance',
      key: 'last_known_balance',
      render: (balance: number, record: any) => {
        if (balance === null || balance === undefined) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const coinSymbol = record.coin_id?.symbol || '';
        return (
          <Tooltip title={`${balance} ${coinSymbol}`}>
            <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {balance.toFixed(6)} <span style={{ color: '#1890ff', fontSize: '11px' }}>{coinSymbol}</span>
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: t('updateBalanceTime', 'Last Balance Update'),
      dataIndex: 'update_balance_time',
      key: 'update_balance_time',
      render: (time: string) => {
        if (!time) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const date = new Date(time);
        const formattedDate = date.toLocaleDateString();
        const formattedTime = date.toLocaleTimeString();
        return (
          <Tooltip title={`${formattedDate} ${formattedTime}`}>
            <span style={{ fontSize: '12px' }}>
              {formattedDate}<br />
              <span style={{ color: '#666' }}>{formattedTime}</span>
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: t('actionsLabel', 'Actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('edit', 'Edit')}
          />
          <Button
            type="default"
            className={record.status === 'Active' ? 'btn-outline-warning' : 'btn-outline-success'}
            size="small"
            icon={<PoweroffOutlined />}
            onClick={() => handleToggleStatus(record)}
            title={record.status === 'Active' ? t('deactivate', 'Deactivate') : t('activate', 'Activate')}
            style={{
              borderColor: record.status === 'Active' ? '#faad14' : '#52c41a',
              color: record.status === 'Active' ? '#faad14' : '#52c41a'
            }}
          />
          <Button
            type="default"
            className="btn-outline-info"
            size="small"
            icon={<WalletOutlined />}
            onClick={() => handleBalanceCheck(record)}
            title={t('checkBalance', 'Check Balance')}
            style={{
              borderColor: '#1890ff',
              color: '#1890ff'
            }}
            loading={isLoadingBalance && selectedWallet?._id === record._id}
          />
          <Button
            type="default"
            className="btn-outline-success"
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleSendTransaction(record)}
            title={t('sendTransaction.send', 'Send (Direct)')}
            style={{
              borderColor: '#52c41a',
              color: '#52c41a'
            }}
            disabled={record.status !== 'Active'}
          />
          <Button
            type="default"
            className="btn-outline-primary"
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleCreateWithdrawal(record)}
            title={t('withdrawal.create', 'Create Withdrawal')}
            style={{
              borderColor: '#1890ff',
              color: '#1890ff'
            }}
            disabled={record.status !== 'Active'}
          />
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{t('title', 'Wallet Management')}</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              {t('refresh', 'Refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={isLoadingCoins || isLoadingNetworks}
            >
              {t('generateWallet', 'Generate Wallet')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Tabs defaultActiveKey="all" onChange={handleTabChange}>
          <TabPane tab={t('allWallets', 'All Wallets')} key="all" />
          <TabPane tab={t('hotWallets', 'Hot Wallets')} key="hot" />
          <TabPane tab={t('warmWallets', 'Warm Wallets')} key="warm" />
          <TabPane tab={t('coldWallets', 'Cold Wallets')} key="cold" />
        </Tabs>

        {/* Search and Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder={t('searchPlaceholder', 'Search by address or user ID')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <SearchableSelect
              placeholder={t('selectCoin', 'Select Coin')}
              searchPlaceholder="Type to search coins..."
              style={{ width: '100%' }}
              value={coinFilter}
              onChange={(value) => {
                setCoinFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isLoadingCoins}
            >
              <Option value="">{t('allCoins', 'All Coins')}</Option>
              {coinsData?.data?.map((coin: any) => (
                <Option key={coin._id} value={coin._id}>
                  {coin.symbol} - {coin.full_name}
                </Option>
              ))}
            </SearchableSelect>
          </Col>
          <Col span={4}>
            <SearchableSelect
              placeholder={t('selectNetwork', 'Select Network')}
              searchPlaceholder="Type to search networks..."
              style={{ width: '100%' }}
              value={networkFilter}
              onChange={(value) => {
                setNetworkFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isLoadingNetworks}
            >
              <Option value="">{t('allNetworks', 'All Networks')}</Option>
              {networksData?.data?.map((network: any) => (
                <Option key={network._id} value={network._id}>
                  {network.title} - {network.full_name || ''}
                </Option>
              ))}
            </SearchableSelect>
          </Col>
          <Col span={4}>
            <SearchableSelect
              placeholder={t('selectStatus', 'Select Status')}
              searchPlaceholder="Type to search status..."
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
            >
              <Option value="">{t('allStatuses', 'All Statuses')}</Option>
              <Option value="Active">{t('active', 'Active')}</Option>
              <Option value="Inactive">{t('inactive', 'Inactive')}</Option>
            </SearchableSelect>
          </Col>
        </Row>

        {/* Wallets Table */}
        <Table
          columns={columns}
          dataSource={walletsData?.data?.wallets || []}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: walletsData?.data?.pagination?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              if (pageSize) setPageSize(pageSize);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} wallets`
          }}
        />
      </Card>

      {/* Add Wallet Modal */}
      <Modal
        title={t('generateWallet', 'Generate Wallet')}
        open={isAddModalVisible}
        onCancel={handleAddModalClose}
        footer={[
          <Button key="cancel" onClick={handleAddModalClose}>
            {t('cancel', 'Cancel')}
          </Button>,
          <Button
            key="generate"
            type="primary"
            onClick={() => form.submit()}
            loading={createWalletMutation.isPending}
          >
            {t('generate', 'Generate Wallet')}
          </Button>
        ]}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddFormSubmit}
        >
          {/* Generated fields (read-only) - shown at the top like in legacy dashboard */}
          <Form.Item
            label={t('publicAddress', 'Public Address (Generated)')}
          >
            <Input
              value={generatedPublicAddress}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </Form.Item>

          <Form.Item
            label={t('privateKey', 'Private Key (Generated)')}
          >
            <Input.Password
              value={generatedPrivateKey}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: privateKeyVisible,
                onVisibleChange: setPrivateKeyVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          {/* User input fields */}
          <Form.Item
            name="user_id"
            label={t('userId', 'User ID')}
            rules={[{ required: true, message: t('userIdRequired', 'Please enter a user ID') }]}
          >
            <Input type="number" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="coin_id"
                label={t('coin', 'Coin')}
                rules={[{ required: true, message: t('coinRequired', 'Please select a coin') }]}
              >
                <SearchableSelect
                  placeholder={t('selectCoin', 'Select Coin')}
                  searchPlaceholder="Type to search coins..."
                  loading={isLoadingCoins}
                  onChange={() => updateAvailableCoinNetworks()}
                >
                  {coinsData?.data?.map((coin: any) => (
                    <Option key={coin._id} value={coin._id}>
                      {coin.symbol} - {coin.full_name}
                    </Option>
                  ))}
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="network_id"
                label={t('network', 'Network')}
                rules={[{ required: true, message: t('networkRequired', 'Please select a network') }]}
              >
                <SearchableSelect
                  placeholder={t('selectNetwork', 'Select Network')}
                  searchPlaceholder="Type to search networks..."
                  loading={isLoadingNetworks}
                  onChange={() => updateAvailableCoinNetworks()}
                >
                  {networksData?.data ? (
                    <>
                      {networksData.data.map((network: any) => (
                        <Option key={network._id} value={network._id}>
                          {network.title} - {network.full_name || ''}
                        </Option>
                      ))}
                    </>
                  ) : (
                    <Option value="" disabled>Loading networks...</Option>
                  )}
                </SearchableSelect>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="coin_network_id"
            label={t('coinNetwork', 'Coin Network')}
            rules={[{ required: true, message: t('coinNetworkRequired', 'Please select a coin network') }]}
          >
            <SearchableSelect
              placeholder={t('selectCoinNetwork', 'Select Coin Network')}
              searchPlaceholder="Type to search coin networks..."
              loading={isLoadingCoinNetworks}
              disabled={availableCoinNetworks.length === 0}
            >
              {availableCoinNetworks.map((cn: any) => (
                <Option key={cn._id} value={cn._id}>
                  {cn.coin_symbol} on {cn.network_title}
                </Option>
              ))}
            </SearchableSelect>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="wallet_type"
                label={t('walletType', 'Wallet Type')}
                initialValue="hot"
                rules={[{ required: true, message: t('walletTypeRequired', 'Please select a wallet type') }]}
              >
                <SearchableSelect
                  placeholder={t('selectWalletType', 'Select Wallet Type')}
                  searchPlaceholder="Type to search wallet types..."
                >
                  <Option value="hot">{t('hot', 'Hot')}</Option>
                  <Option value="warm">{t('warm', 'Warm')}</Option>
                  <Option value="cold">{t('cold', 'Cold')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('statusLabel', 'Status')}
                initialValue="Active"
                rules={[{ required: true, message: t('statusRequired', 'Please select a status') }]}
              >
                <SearchableSelect
                  placeholder={t('selectStatus', 'Select Status')}
                  searchPlaceholder="Type to search status..."
                >
                  <Option value="Active">{t('active', 'Active')}</Option>
                  <Option value="Inactive">{t('inactive', 'Inactive')}</Option>
                </SearchableSelect>
              </Form.Item>
            </Col>
          </Row>

          {/* Seed field (read-only) */}
          <Form.Item
            label={t('seed', 'Seed (Generated)')}
          >
            <Input.Password
              value={generatedSeed}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: seedVisible,
                onVisibleChange: setSeedVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            name="memo"
            label={t('memo', 'Memo')}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Wallet Modal */}
      <Modal
        title={t('editWallet', 'Edit Wallet')}
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setPrivateKeyVisible(false);
          setSeedVisible(false);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditFormSubmit}
        >
          {/* Read-only fields */}
          <Form.Item
            label={t('publicAddress', 'Public Address (Generated)')}
          >
            <Input
              value={selectedWallet?.public_address || selectedWallet?.address}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </Form.Item>

          <Form.Item
            label={t('privateKey', 'Private Key (Generated)')}
          >
            <Input.Password
              value={selectedWallet?.private_key}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: privateKeyVisible,
                onVisibleChange: setPrivateKeyVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('userId', 'User ID')}
              >
                <Input
                  value={selectedWallet?.user_id || selectedWallet?.userid}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('walletType', 'Wallet Type')}
              >
                <Select
                  value={(selectedWallet?.wallet_type || selectedWallet?.type || '').toLowerCase()}
                  disabled
                  style={{ backgroundColor: '#f0f0f0' }}
                >
                  <Option value="hot">{t('hot', 'Hot')}</Option>
                  <Option value="warm">{t('warm', 'Warm')}</Option>
                  <Option value="cold">{t('cold', 'Cold')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('coin', 'Coin')}
              >
                <Input
                  value={selectedWallet?.coin_symbol ||
                    (selectedWallet?.coin_id && typeof selectedWallet.coin_id === 'object' ?
                      selectedWallet.coin_id.symbol : '') ||
                    (selectedWallet?.coin && typeof selectedWallet.coin === 'string' ?
                      selectedWallet.coin : '')}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('network', 'Network')}
              >
                <Input
                  value={selectedWallet?.network_title ||
                    (selectedWallet?.network_id && typeof selectedWallet.network_id === 'object' ?
                      selectedWallet.network_id.title : '') ||
                    (selectedWallet?.network && typeof selectedWallet.network === 'string' ?
                      selectedWallet.network : '')}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Editable field */}
          <Form.Item
            name="status"
            label={t('statusLabel', 'Status')}
            rules={[{ required: true, message: t('statusRequired', 'Please select a status') }]}
          >
            <SearchableSelect
              placeholder={t('selectStatus', 'Select Status')}
              searchPlaceholder="Type to search status..."
            >
              <Option value="Active">{t('active', 'Active')}</Option>
              <Option value="Inactive">{t('inactive', 'Inactive')}</Option>
            </SearchableSelect>
          </Form.Item>

          <Form.Item
            label={t('seed', 'Seed (Generated)')}
          >
            <Input.Password
              value={selectedWallet?.seed || selectedWallet?.mnemonic}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: seedVisible,
                onVisibleChange: setSeedVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            label={t('memo', 'Memo')}
          >
            <Input.TextArea
              value={selectedWallet?.memo || ''}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              rows={2}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => {
                setIsEditModalVisible(false);
                setPrivateKeyVisible(false);
                setSeedVisible(false);
              }}>
                {t('cancel', 'Cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={updateWalletStatusMutation.isPending}>
                {t('save', 'Save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Wallet Creation Result Modal */}
      <Modal
        title={t('generateWallet', 'Generate Wallet')}
        open={isResultModalVisible}
        onCancel={() => setIsResultModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsResultModalVisible(false)}>
            {t('close', 'Close')}
          </Button>
        ]}
        width={600}
      >
        <Divider />
        <Form layout="vertical">
          <Form.Item
            label={t('publicAddress', 'Public Address (Generated)')}
          >
            <Input
              value={createdWallet?.public_address || createdWallet?.address}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </Form.Item>

          <Form.Item
            label={t('privateKey', 'Private Key (Generated)')}
          >
            <Input.Password
              value={createdWallet?.private_key || createdWallet?.privateKey}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: privateKeyVisible,
                onVisibleChange: setPrivateKeyVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('userId', 'User ID')}
              >
                <Input
                  value={createdWallet?.user_id || createdWallet?.userid}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('walletType', 'Wallet Type')}
              >
                <Input
                  value={createdWallet?.wallet_type || createdWallet?.type || 'hot'}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('coin', 'Coin')}
              >
                <Input
                  value={createdWallet?.coin_symbol || createdWallet?.coin}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('network', 'Network')}
              >
                <Input
                  value={createdWallet?.network_title || createdWallet?.network}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t('statusLabel', 'Status')}
          >
            <Input
              value={createdWallet?.status || 'Active'}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </Form.Item>

          <Form.Item
            label={t('seed', 'Seed (Generated)')}
          >
            <Input.Password
              value={createdWallet?.seed || createdWallet?.mnemonic}
              readOnly
              style={{ backgroundColor: '#f0f0f0' }}
              visibilityToggle={{
                visible: seedVisible,
                onVisibleChange: setSeedVisible
              }}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          {createdWallet?.memo && (
            <Form.Item
              label={t('memo', 'Memo')}
            >
              <Input.TextArea
                value={createdWallet?.memo}
                readOnly
                style={{ backgroundColor: '#f0f0f0' }}
                rows={2}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Balance Modal */}
      <Modal
        title={
          <div>
            <WalletOutlined style={{ marginRight: 8 }} />
            {t('balanceTitle', 'Wallet Balance')}
          </div>
        }
        open={isBalanceModalVisible}
        onCancel={() => setIsBalanceModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsBalanceModalVisible(false)}>
            {t('close', 'Close')}
          </Button>
        ]}
        width={600}
      >
        {isLoadingBalance ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('loadingBalance', 'Loading balance information...')}
            </div>
          </div>
        ) : balanceData ? (
          <div style={{ marginTop: 16 }}>
            {/* Wallet Information */}
            <Descriptions bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('address', 'Address')}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                    {selectedWallet?.public_address}
                  </span>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(selectedWallet?.public_address);
                      message.success(t('addressCopied', 'Address copied to clipboard'));
                    }}
                    title={t('copyAddress', 'Copy address to clipboard')}
                  />
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t('userId', 'User ID')}>
                {selectedWallet?.user_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('statusLabel', 'Status')}>
                <Tag color={selectedWallet?.status === 'Active' ? 'success' : 'error'}>
                  {selectedWallet?.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('walletType', 'Wallet Type')}>
                <Tag color={selectedWallet?.wallet_type === 'HOT' ? 'blue' : 'orange'}>
                  {selectedWallet?.wallet_type || '-'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('network', 'Network')}>
                {selectedWallet?.network_id?.title || selectedWallet?.network_id?.full_name || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* Balances Table */}
            <div style={{ marginTop: 16 }}>
              <Title level={5}>{t('balances', 'Balances')}</Title>
              {balanceData.data?.balances && balanceData.data.balances.length > 0 ? (
                <Table
                  dataSource={balanceData.data.balances}
                  pagination={false}
                  size="small"
                  rowKey={(record: any) => `${record.type}-${record.symbol}-${record.contract_address || 'native'}`}
                  columns={[
                    {
                      title: t('type', 'Type'),
                      dataIndex: 'type',
                      key: 'type',
                      width: 80,
                      render: (type: string) => (
                        <Tag color={type === 'native' ? 'blue' : 'green'}>
                          {type === 'native' ? t('native', 'Native') : t('token', 'Token')}
                        </Tag>
                      )
                    },
                    {
                      title: t('symbol', 'Symbol'),
                      dataIndex: 'symbol',
                      key: 'symbol',
                      width: 100,
                      render: (symbol: string) => (
                        <span style={{ fontWeight: 'bold' }}>{symbol}</span>
                      )
                    },
                    {
                      title: t('balance', 'Balance'),
                      dataIndex: 'balance',
                      key: 'balance',
                      width: 150,
                      render: (balance: string, record: any) => (
                        <span style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                          {parseFloat(balance).toFixed(6)} {record.symbol}
                        </span>
                      )
                    },
                    {
                      title: t('usdValue', 'USD Value'),
                      dataIndex: 'balanceInUSD',
                      key: 'balanceInUSD',
                      width: 120,
                      render: (usdValue: string) => (
                        usdValue ? (
                          <span style={{ color: 'green' }}>
                            ${parseFloat(usdValue).toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )
                      )
                    },
                    {
                      title: t('irtValue', 'IRT Value'),
                      dataIndex: 'balanceInIRT',
                      key: 'balanceInIRT',
                      width: 150,
                      render: (irtValue: string) => (
                        irtValue ? (
                          <span style={{ color: 'blue' }}>
                            {Number(irtValue).toLocaleString()} IRT
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )
                      )
                    },
                    {
                      title: t('contractAddress', 'Contract Address'),
                      dataIndex: 'contract_address',
                      key: 'contract_address',
                      render: (address: string) => (
                        address ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              maxWidth: '150px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {address}
                            </span>
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                navigator.clipboard.writeText(address);
                                message.success(t('contractAddressCopied', 'Contract address copied to clipboard'));
                              }}
                              title={t('copyContractAddress', 'Copy contract address')}
                            />
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )
                      )
                    }
                  ]}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  {t('noBalancesFound', 'No balances found for this wallet')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <div style={{ marginTop: 16 }}>
              {t('noBalanceData', 'No balance information available')}
            </div>
          </div>
        )}
      </Modal>

      {/* Send Transaction Modal */}
      <SendTransactionModal
        visible={isSendModalVisible}
        onCancel={() => setIsSendModalVisible(false)}
        walletData={sendWalletData}
      />

      {/* Withdrawal Modal */}
      <WithdrawalModal
        visible={isWithdrawalModalVisible}
        onCancel={() => setIsWithdrawalModalVisible(false)}
        walletData={withdrawalWalletData}
      />
    </div>
  );
};

export default WalletManagementPage;
